import { Fragment } from 'react';
import type { ReactNode } from 'react';

export interface WikiMarkdownProps {
  /** 表示する Markdown 本文 */
  markdown: string;
}

/**
 * バッククォート囲みをインラインコードへ変換する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business Markdown 内のインラインコード記法を code 要素へ変換する
 * @graph-connects none
 */
function renderInline(text: string): ReactNode[] {
  return text.split(/(`[^`]+`)/g).map((part, i) =>
    part.startsWith('`') && part.endsWith('`') && part.length > 2 ? (
      <code
        key={i}
        className="px-1 py-0.5 mx-0.5 bg-[var(--color-bg-base)] border border-[var(--color-border)] rounded text-[0.85em] font-mono"
      >
        {part.slice(1, -1)}
      </code>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}

/** 箇条書き行から中身を取り出す。リストでなければ null。 @graph-connects none */
function listItemText(line: string): string | null {
  const match = /^\s*(?:[-*+]|\d+\.)\s+(.*)$/.exec(line);
  return match ? (match[1] ?? '') : null;
}

/**
 * 連続した箇条書きを ul にまとめ、それ以外は段落として描画する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business コードブロック以外の散文を見出し・箇条書き・段落として描画する
 * @graph-connects none
 */
function renderProse(text: string, keyPrefix: string): ReactNode[] {
  const blocks: ReactNode[] = [];
  const lines = text.split('\n');
  let listBuffer: string[] = [];

  const flushList = (): void => {
    if (listBuffer.length === 0) return;
    blocks.push(
      <ul
        key={`${keyPrefix}-ul-${String(blocks.length)}`}
        className="my-2 pl-5 list-disc space-y-1 text-[var(--color-text-secondary)]"
      >
        {listBuffer.map((item, i) => (
          <li key={i}>{renderInline(item)}</li>
        ))}
      </ul>,
    );
    listBuffer = [];
  };

  for (const line of lines) {
    const item = listItemText(line);
    if (item !== null) {
      listBuffer.push(item);
      continue;
    }
    flushList();

    if (line.trim() === '') continue;

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push(
        <p
          key={`${keyPrefix}-h-${String(blocks.length)}`}
          className="mt-3 mb-1 font-semibold text-[var(--color-text-primary)]"
        >
          {renderInline(heading[2] ?? '')}
        </p>,
      );
      continue;
    }

    blocks.push(
      <p
        key={`${keyPrefix}-p-${String(blocks.length)}`}
        className="my-1 text-[var(--color-text-secondary)] leading-relaxed"
      >
        {renderInline(line)}
      </p>,
    );
  }
  flushList();

  return blocks;
}

/**
 * wiki 本文の Markdown を描画する。
 *
 * 構造化した項目でも本文（`markdown`）はコードブロックと散文が混ざるため、
 * フェンス単位で分割してコードだけ等幅・横スクロールで見せる。外部の Markdown
 * ライブラリは入れず、この画面が実際に使う記法（フェンス・インラインコード・
 * 見出し・箇条書き）だけを扱う。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business wiki 記事の Markdown 本文をコードブロック・散文に分けて描画する
 * @graph-connects none
 */
export function WikiMarkdown({ markdown }: WikiMarkdownProps): ReactNode {
  const parts = markdown.split(/(```[\s\S]*?```)/g);

  return (
    <div className="text-sm">
      {parts.map((part, index) => {
        if (part.startsWith('```') && part.endsWith('```') && part.length > 6) {
          const inner = part.slice(3, -3);
          const firstNewline = inner.indexOf('\n');
          const language = firstNewline > 0 ? inner.slice(0, firstNewline).trim() : '';
          const code = firstNewline > 0 ? inner.slice(firstNewline + 1) : inner;
          return (
            <div
              key={index}
              className="my-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] overflow-hidden"
            >
              {language !== '' && (
                <div className="px-3 py-1 text-xs text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                  {language}
                </div>
              )}
              <pre className="p-3 overflow-x-auto">
                <code className="text-xs font-mono text-[var(--color-text-primary)] whitespace-pre">
                  {code.replace(/\n$/, '')}
                </code>
              </pre>
            </div>
          );
        }
        return <Fragment key={index}>{renderProse(part, `p${String(index)}`)}</Fragment>;
      })}
    </div>
  );
}
